'use client';

import { useEffect, useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import FadeInSection from '@/components/ui/FadeInSection';
import FaqAccordion, { type FaqItem } from '@/components/ui/FaqAccordion';

// Localized "Month Year" for the "Last updated" stamp. `ua` is this site's
// segment for Ukrainian; map it to the BCP-47 tag `uk` so Intl resolves it.
function formatMonthYear(locale: string) {
  const intlLocale = locale === 'ua' ? 'uk' : locale;
  // Slavic locales (pl/uk/ru) lower-case month names by grammar, but the
  // "Last updated" stamp wants a capitalized month — upper-case the first
  // letter (the month leads the string in every locale we ship).
  const formatted = new Intl.DateTimeFormat(intlLocale, { month: 'long', year: 'numeric' }).format(new Date());
  return formatted.charAt(0).toLocaleUpperCase(intlLocale) + formatted.slice(1);
}

export default function FaqSection() {
  const t = useTranslations('faq');
  const locale = useLocale();
  const items = t.raw('items') as FaqItem[];

  // "Last updated: <Month> <Year>" — recomputed on mount so it always follows
  // the real current date on the client instead of freezing at build time.
  const [updated, setUpdated] = useState(() => formatMonthYear(locale));
  useEffect(() => {
    setUpdated(formatMonthYear(locale));
  }, [locale]);

  return (
    <section
      id="faq"
      className="scroll-mt-2 border-t border-surface-bright/15 px-margin-mobile py-section-gap lg:scroll-mt-4 lg:px-margin-desktop"
    >
      <div className="mx-auto w-full max-w-3xl">
        <FadeInSection direction="right" distance={64}>
          <p className="text-label-caps uppercase tracking-widest text-primary">
            {t('eyebrow')}
            <span
              suppressHydrationWarning
              className="ml-2 font-normal normal-case tracking-normal text-on-surface-variant/70"
            >
              · {t('updated')}: {updated}
            </span>
          </p>
          <h2 className="mt-stack-md font-headline text-headline-lg font-bold tracking-[0.02em] text-on-surface">
            {t('headline')}
          </h2>
        </FadeInSection>

        <FaqAccordion items={items} />
      </div>
    </section>
  );
}
